import { Fade, type PopoverProps } from "@mui/material";
import type { FunctionComponent } from "react";
import { useCallback, useMemo, useState } from "react";
import { graphql, useFragment } from "react-relay";

import { useResponsive } from "shared/hooks";
import { theme } from "shared/lib/styles";
import { notEmpty } from "shared/lib/utils";

import {
  ButtonText,
  Container,
  Divider,
  ItemLinksContainer,
  Label,
  LabelAndItemLinksContainer,
  LabelHeader,
  MenuTitle,
  MenuTitleStatic,
  PopoverHeader,
  PopoverMain,
  StyledButton,
  StyledEntityLink,
  StyledIconArrowDown,
  StyledIconClose,
  StyledPopover,
} from "scmp-app/components/video/video-header-dropdown-menu/styles";
import { pathname as videoPathname } from "scmp-app/pages/video";
import type { videoHeaderDropdownMenuQuery$key } from "scmp-app/queries/__generated__/videoHeaderDropdownMenuQuery.graphql";

type Props = {
  activeEntityId?: string;
  className?: string;
  reference: videoHeaderDropdownMenuQuery$key;
  withCloseButton?: boolean;
};

export const VideoHeaderDropdownMenu: FunctionComponent<Props> = ({
  activeEntityId,
  className,
  reference,
  withCloseButton = true,
}) => {
  const { videoHighlightMenu, videoSectionMenu, videoSeriesMenu } = useFragment(
    graphql`
      fragment videoHeaderDropdownMenuQuery on Query
      @argumentDefinitions(withHighlight: { type: "Boolean!" }) {
        videoHighlightMenu: queue(filter: { name: "video_highlight_menu" })
          @include(if: $withHighlight) {
          items(first: 20) {
            edges {
              node {
                ... on Section {
                  entityId
                  name
                }
                ... on Topic {
                  entityId
                  name
                }
                ... on VideoSection {
                  entityId
                  name
                }
                ...entityLink
              }
            }
          }
        }
        videoSeriesMenu: queue(filter: { name: "video_series_menu" }) {
          items(first: 20) {
            edges {
              node {
                ... on Section {
                  entityId
                  name
                }
                ... on Topic {
                  entityId
                  name
                }
                ... on VideoSection {
                  entityId
                  name
                }
                ...entityLink
              }
            }
          }
        }
        videoSectionMenu: queue(filter: { name: "video_sections_menu" }) {
          items(first: 20) {
            edges {
              node {
                ... on Section {
                  entityId
                  name
                }
                ... on Topic {
                  entityId
                  name
                }
                ... on VideoSection {
                  entityId
                  name
                }
                ...entityLink
              }
            }
          }
        }
      }
    `,
    reference,
  );

  const [anchorElement, setAnchorElement] = useState<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);

  const isDesktop = useResponsive(theme.breakpoints.up("desktop"), false);

  const popoverProps = useMemo<Partial<PopoverProps>>(
    () =>
      isDesktop
        ? {
            anchorOrigin: { horizontal: "right", vertical: "bottom" },
            slots: {
              transition: Fade,
            },
            transformOrigin: { horizontal: "right", vertical: "top" },
          }
        : {
            slots: {
              transition: Fade,
            },
          },
    [isDesktop],
  );

  const menuItems = useMemo(
    () => ({
      highlight:
        videoHighlightMenu?.items?.edges?.filter(edge => notEmpty(edge.node.entityId)) ?? [],
      section: videoSectionMenu?.items?.edges?.filter(edge => notEmpty(edge.node.entityId)) ?? [],
      series: videoSeriesMenu?.items?.edges?.filter(edge => notEmpty(edge.node.entityId)) ?? [],
    }),
    [
      videoHighlightMenu?.items?.edges,
      videoSectionMenu?.items?.edges,
      videoSeriesMenu?.items?.edges,
    ],
  );

  const isOnVideoHomepage = !activeEntityId;

  const captureElement = useCallback((element: HTMLDivElement | null) => {
    if (!notEmpty(element)) return;
    setAnchorElement(element);
  }, []);

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <Container className={className}>
      <StyledButton onClick={handleOpen} ref={captureElement}>
        <ButtonText>All Videos</ButtonText>
        <StyledIconArrowDown />
      </StyledButton>

      <StyledPopover {...popoverProps} anchorEl={anchorElement} onClose={handleClose} open={open}>
        <PopoverHeader>
          {withCloseButton && <StyledIconClose onClick={handleClose} />}
        </PopoverHeader>
        <PopoverMain>
          <Divider />
          {isOnVideoHomepage ? (
            <MenuTitleStatic>Latest Videos</MenuTitleStatic>
          ) : (
            <MenuTitle onClick={handleClose} pathname={videoPathname}>
              Latest Videos
            </MenuTitle>
          )}

          {notEmpty(menuItems?.highlight) && (
            <LabelAndItemLinksContainer>
              <LabelHeader>
                <Divider />
                <Label>highlight</Label>
              </LabelHeader>
              <ItemLinksContainer>
                {menuItems?.highlight?.map(({ node }) => (
                  <StyledEntityLink
                    $isActive={node.entityId === activeEntityId || undefined}
                    key={node.entityId}
                    reference={node}
                  >
                    {node.name}
                  </StyledEntityLink>
                ))}
              </ItemLinksContainer>
            </LabelAndItemLinksContainer>
          )}

          {notEmpty(menuItems?.series) && (
            <LabelAndItemLinksContainer>
              <LabelHeader>
                <Divider />
                <Label>series</Label>
              </LabelHeader>
              <ItemLinksContainer>
                {menuItems.series.map(({ node }) => (
                  <StyledEntityLink
                    $isActive={node.entityId === activeEntityId || undefined}
                    key={node.entityId}
                    reference={node}
                  >
                    {node.name}
                  </StyledEntityLink>
                ))}
              </ItemLinksContainer>
            </LabelAndItemLinksContainer>
          )}

          {notEmpty(menuItems.section) && (
            <LabelAndItemLinksContainer>
              <LabelHeader>
                <Divider />
                <Label>sections</Label>
              </LabelHeader>
              <ItemLinksContainer>
                {menuItems.section.map(({ node }) => (
                  <StyledEntityLink
                    $isActive={node.entityId === activeEntityId || undefined}
                    key={node.entityId}
                    reference={node}
                  >
                    {node.name}
                  </StyledEntityLink>
                ))}
              </ItemLinksContainer>
            </LabelAndItemLinksContainer>
          )}

          <Divider />
        </PopoverMain>
      </StyledPopover>
    </Container>
  );
};

VideoHeaderDropdownMenu.displayName = "VideoHeaderDropdownMenu";
